import { ParamDeclaration, ResolveTypes, Transition } from '@uirouter/angular';
import { IObject } from '@type_definitions/datatypes';
import _ from 'underscore';
import { util } from '@app/shared/util';
import { RouterConfigType, TRANSLATION_READY_RESOLVER } from '@app/app-routes.config';

interface IReportParam {
  longParamName?:ReportParameterCode;
  shortParamName?:ReportParameterShortCode;
  config?:ParamDeclaration;
}

export enum ReportParameterCode {
  BROWSELIST_CODE = 'listCode',
  REPORT_ID = 'reportId',
  MENU_ID = 'menuId', // coming from GA1 'MenuTreeView'
  TITLE = 'title',
  SHOW_FILTERS = 'showfilters',
  AUTO_LOAD = 'autoload',
  EXPORT_TEMPLATE = 'exportTemplate',
  FILTERS = 'filters',
  STORED_REPORT = 'storedReport',
  SHOW_COMMENTS = 'showcomments',
  FORM_CONTEXT = 'formContext'
}

export enum ReportParameterShortCode {
  TITLE = 't',
  SHOW_FILTERS = 'sf',
  AUTO_LOAD = 'a',
  EXPORT_TEMPLATE = 'et',
  FILTERS = 'f',
  STORED_REPORT = 'sr',
  SHOW_COMMENTS = 'sc',
  FORM_CONTEXT = 'fc',
  LAYOUT_SETTING = 'l'
}

const REPORT_PARAMS:IReportParam[] = [
  { longParamName: ReportParameterCode.MENU_ID }, // menu id doesn't need short parameter name
  { longParamName: ReportParameterCode.TITLE, shortParamName: ReportParameterShortCode.TITLE },
  { longParamName: ReportParameterCode.SHOW_FILTERS, shortParamName: ReportParameterShortCode.SHOW_FILTERS },
  { longParamName: ReportParameterCode.AUTO_LOAD, shortParamName: ReportParameterShortCode.AUTO_LOAD },
  { longParamName: ReportParameterCode.EXPORT_TEMPLATE, shortParamName: ReportParameterShortCode.EXPORT_TEMPLATE },
  {
    longParamName: ReportParameterCode.FILTERS,
    shortParamName: ReportParameterShortCode.FILTERS,
    config: {
      dynamic: true,
      type: RouterConfigType.RAW
    }
  },
  {
    longParamName: ReportParameterCode.STORED_REPORT,
    shortParamName: ReportParameterShortCode.STORED_REPORT,
    config: {
      dynamic: true
    }
  },
  {
    longParamName: ReportParameterCode.SHOW_COMMENTS,
    shortParamName: ReportParameterShortCode.SHOW_COMMENTS,
    config: {
      type: 'string',
      value: 'true' // default value
    }
  },
  {
    longParamName: ReportParameterCode.FORM_CONTEXT,
    shortParamName: ReportParameterShortCode.FORM_CONTEXT,
    config: {
      dynamic: true
    }
  },
  {
    // layout setting is new parameter so it doesn't need long parameter name
    shortParamName: ReportParameterShortCode.LAYOUT_SETTING,
    config: {
      dynamic: true
    }
  }
];

/**
 * A string representing the concatenated report query parameters for use in URLs.
 *
 * The variable transforms an array of report parameters (REPORT_PARAMS) into a string
 * where each parameter is represented by its long and short name, concatenated with an ampersand (&).
 *
 * example: 'showfilters&sf&showcomments&sc...'
 * this is to be used in UI-router state declaration
 */
export const REPORT_QUERY_PARAMS:string = _.map(REPORT_PARAMS, (param:IReportParam) => _.compact([param.longParamName, param.shortParamName])).flat().join('&');

/**
 * The `PARAMS_CONFIG` variable stores UI-router configuration parameters used in report and browselist.
 * This object holds key-value pairs of short and long parameter names where the keys are parameter names and the values are the corresponding settings.
 */
export const REPORT_PARAMS_DECLARATION:IObject = {};

_.each(REPORT_PARAMS, (param:IReportParam) => {
  if (param.config) {
    // define configuration for long parameter name and it should not have default value
    REPORT_PARAMS_DECLARATION[param.longParamName] = { ...param.config, value: undefined };
    // define configuration for short parameter name
    REPORT_PARAMS_DECLARATION[param.shortParamName] = param.config;
  }
});

/**
 * REPORT_PARAMS_RESOLVER is an array of ResolveTypes used for resolving various state and query parameters
 * related to reporting functionality. It includes predefined resolvers as well as dynamically generated
 * resolvers for URL path and query parameters.
 *
 * The array contains:
 * - TRANSLATION_READY_RESOLVER: A predefined resolver.
 * - Path parameters resolvers for 'browselistCode', 'listCode', 'reportId'.
 * - Query parameters resolvers for 'showFilters', 'showComments', 'autoload', 'title', 'storedReport',
 *   'templateCode', 'formContext', 'layoutSettingToken'
 *
 * Each state parameter resolver is created using the `stateParamsResolvers` function, with specific mappings
 * and, in some cases, default values.
 */
export const REPORT_PARAMS_RESOLVER:ResolveTypes[] = [
  TRANSLATION_READY_RESOLVER,
  // url path parameters
  stateParamsResolvers('browselistCode', ReportParameterCode.BROWSELIST_CODE),
  stateParamsResolvers('reportId', ReportParameterCode.REPORT_ID),
  // url query parameters
  stateParamsResolvers('menuId', ReportParameterCode.MENU_ID, undefined, (param:string) => Number(param) || undefined),
  stateParamsResolvers('showFilters', ReportParameterCode.SHOW_FILTERS, ReportParameterShortCode.SHOW_FILTERS, (param:string) => param === 'true'),
  stateParamsResolvers('showComments', ReportParameterCode.SHOW_COMMENTS, ReportParameterShortCode.SHOW_COMMENTS, (param:string) => param === 'true'),
  stateParamsResolvers('autoload', ReportParameterCode.AUTO_LOAD, ReportParameterShortCode.AUTO_LOAD, (param:string) => param === 'true'),
  stateParamsResolvers('title', ReportParameterCode.TITLE, ReportParameterShortCode.TITLE),
  stateParamsResolvers('storedReport', ReportParameterCode.STORED_REPORT, ReportParameterShortCode.STORED_REPORT),
  stateParamsResolvers('templateCode', ReportParameterCode.EXPORT_TEMPLATE, ReportParameterShortCode.EXPORT_TEMPLATE),
  stateParamsResolvers('formContext', ReportParameterCode.FORM_CONTEXT, ReportParameterShortCode.FORM_CONTEXT),
  stateParamsResolvers('layoutSettingToken', undefined, ReportParameterShortCode.LAYOUT_SETTING)
];

/**
 * Generates an object of state parameter resolvers based on the provided parameters.
 *
 * @param {string} token - A unique identifier for the resolver, this is points to component class fields.
 * @param {ReportParameterCode} longParameterName - The optional long form of the report parameter name to resolve.
 * @param {ReportParameterShortCode} [shortParameterName] - The optional short form of the report parameter name to resolve.
 * @param {function(string): any} [paramResolver] - An optional function to resolve or transform the parameter value.
 * @return {ResolveTypes} An object representing the resolved parameters and their dependencies.
 */
export function stateParamsResolvers(token:string, longParameterName?:ReportParameterCode, shortParameterName?:ReportParameterShortCode, paramResolver?:(param:string) => any):ResolveTypes {
  return {
    token,
    deps: [Transition],
    resolveFn: (transition:Transition) => {
      const params:IObject = transition.params();
      const paramValue:string = util.notNullOrUndefinedOrEmpty(params?.[longParameterName]) ? params[longParameterName] : params[shortParameterName];
      return paramResolver ? paramResolver(paramValue) : paramValue;
    }
  };
}